import { getProfile, get2FAData, get2FTAuth } from "api/graphQL_users";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import Banner from "./components/Banner";
import DeleteAccountModal from "./components/DeleteAccountModal";
import General from "./components/General";
import DivLoader from "components/divloader/DivLoader";

const ProfileOverview = () => {
  const [profileData, setProfileData] = useState<any>();
  const [FAData, setFAData] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>();

  const fetchProfile = () => {
    setIsLoading(true);
    getProfile()
      .then((data) => {
        setProfileData(data[0]);
        if (!data[0]?.auth_2fa) {
          get2FAData()
            .then((d) => setFAData(d))
            .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong"))
            .finally(() => setIsLoading(false));
        } else { setIsLoading(false); }
      })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong"))
      .finally(() => setIsLoading(false));
  };

  const fetch2fa = () => {
    setIsLoading(true);
    get2FTAuth({ enable_2FA: false, user_id: profileData?.id })
      .then(() => { fetchProfile(); toast.success("Two-Factor Authentication Deactivated"); })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong"))
      .finally(() => {});
  };

  useEffect(() => { fetchProfile(); }, []);

  return (
    <div className="po-root">
      {isLoading ? (
        <div className="po-loading">
          <div className="po-spinner" />
          <p className="po-loading-txt">Loading/updating profile…</p>
        </div>
      ) : (
        <>
          <div className="po-page-header">
            <div className="po-title-block">
              <div className="po-page-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div>
                <h1 className="po-heading">My Profile</h1>
                <p className="po-sub">Manage your account details and security settings</p>
              </div>
            </div>
          </div>

          <div className="po-grid">
            <div className="po-col-banner"><Banner profileData={profileData} /></div>
            <div className="po-col-general">
              <General profileData={profileData} fetchProfile={fetchProfile} data2FA={FAData} fetch2fa={fetch2fa} />
            </div>
            <div className="po-col-danger">
              <div className="po-danger-card">
                <div className="po-danger-icon-wrap">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14H6L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                    <path d="M9 6V4h6v2"/>
                  </svg>
                </div>
                <div>
                  <h3 className="po-danger-title">Delete Account</h3>
                  <p className="po-danger-desc">Permanently delete your account and all associated data. This action cannot be undone.</p>
                </div>
                <div className="po-danger-divider" />
                <DeleteAccountModal />
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .po-root { font-family:'Plus Jakarta Sans',sans-serif; display:flex; flex-direction:column; gap:20px; }

        .po-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:70vh; gap:14px; }
        .po-spinner { width:32px; height:32px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:poSpin .7s linear infinite; }
        @keyframes poSpin { to{transform:rotate(360deg)} }
        .po-loading-txt { font-size:13.5px; color:#94a3b8; font-weight:500; }

        .po-page-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px; }
        .po-title-block { display:flex; align-items:center; gap:14px; }
        .po-page-icon { width:44px; height:44px; border-radius:12px; background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; display:flex; align-items:center; justify-content:center; color:#2563eb; flex-shrink:0; }
        .po-heading { font-size:20px; font-weight:800; color:#0f172a; letter-spacing:-0.4px; margin-bottom:2px; line-height:1.2; }
        .po-sub { font-size:13px; color:#94a3b8; font-weight:500; }

        /* ── Desktop: 3 columns ── */
        .po-grid {
          display: grid;
          grid-template-columns: 260px 1fr 220px;
          gap: 18px;
          align-items: start;
        }
        .po-col-banner  { grid-column: 1; }
        .po-col-general { grid-column: 2; }
        .po-col-danger  { grid-column: 3; }

        .po-danger-card {
          background:#fff; border:1.5px solid #fecdd3; border-radius:18px;
          padding:22px; display:flex; flex-direction:column; gap:14px;
          box-shadow:0 2px 8px rgba(225,29,72,.06);
        }
        .po-danger-icon-wrap { width:42px; height:42px; border-radius:11px; background:linear-gradient(135deg,#fff1f2,#ffe4e6); border:1.5px solid #fecdd3; display:flex; align-items:center; justify-content:center; color:#e11d48; flex-shrink:0; }
        .po-danger-title { font-size:14px; font-weight:800; color:#0f172a; margin-bottom:4px; line-height:22px; }
        .po-danger-desc { font-size:12px; color:#94a3b8; font-weight:500; line-height:1.55; }
        .po-danger-divider { height:1px; background:#fef2f2; }

        /* ── Tablet (≤1100px): banner + general side by side, danger full width ── */
        @media (max-width: 1100px) {
          .po-grid { grid-template-columns: 240px 1fr; }
          .po-col-banner  { grid-column: 1; }
          .po-col-general { grid-column: 2; }
          .po-col-danger  { grid-column: 1 / -1; }
        }

        /* ── Small tablet (≤768px): banner + general stacked, danger below ── */
        @media (max-width: 768px) {
          .po-grid { grid-template-columns: 1fr 1fr; gap: 14px; }
          .po-col-banner  { grid-column: 1; }
          .po-col-general { grid-column: 2; }
          .po-col-danger  { grid-column: 1 / -1; }
          .po-heading { font-size: 17px; }
          .po-page-icon { width: 38px; height: 38px; border-radius: 10px; }
        }

        /* ── Mobile (≤600px): fully stacked ── */
        @media (max-width: 600px) {
          .po-grid { grid-template-columns: 1fr; gap: 14px; }
          .po-col-banner, .po-col-general, .po-col-danger { grid-column: 1; }
          .po-danger-card { padding: 18px; }
          .po-heading { font-size: 16px; }
        }

        @media (max-width: 380px) {
          .po-grid { gap: 12px; }
          .po-danger-card { padding: 16px; border-radius: 14px; }
        }
      `}</style>
    </div>
  );
};

export default ProfileOverview;